import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import { TimelyComparisonBucket, TimelyComparisonPeriod } from "./types";

const BUCKET_LABEL_PREFIX: Record<TimelyComparisonPeriod, string> = {
  weekly: "Week",
  monthly: "Month",
  quarterly: "Quarter",
  half_yearly: "Half Year",
  annually: "Year",
};

/** Default mock/API pool sizes — not the displayed count. */
const DEFAULT_POOL_SIZE: Record<TimelyComparisonPeriod, number> = {
  weekly: 52,
  monthly: 36,
  quarterly: 20,
  half_yearly: 12,
  annually: 10,
};

const MIN_BUCKET_PX_BY_PERIOD: Record<TimelyComparisonPeriod, number> = {
  weekly: 96,
  monthly: 84,
  quarterly: 96,
  half_yearly: 96,
  annually: 56,
};

const MIN_LABEL_PX_BY_PERIOD: Record<TimelyComparisonPeriod, number> = {
  weekly: 84,
  monthly: 64,
  quarterly: 76,
  half_yearly: 84,
  annually: 44,
};

const MAX_VISIBLE_BUCKETS: Record<TimelyComparisonPeriod, number> = {
  weekly: 10,
  monthly: 12,
  quarterly: 10,
  half_yearly: 10,
  annually: 12,
};

export const getBucketLabelPrefix = (period: TimelyComparisonPeriod): string =>
  BUCKET_LABEL_PREFIX[period];

export const getDefaultHistoricalPoolSize = (
  period: TimelyComparisonPeriod,
): number => DEFAULT_POOL_SIZE[period];

export const formatBucketDateRange = (
  startDate: string,
  endDate: string,
): string => `${startDate} - ${endDate}`;

const getBucketDateRange = (
  period: TimelyComparisonPeriod,
  bucketsFromEnd: number,
  referenceDate: Date,
): { startDate: Date; endDate: Date } => {
  const offset = bucketsFromEnd - 1;

  switch (period) {
    case "weekly": {
      const end = subDays(referenceDate, offset * 7);
      const start = subDays(end, 6);
      return { startDate: start, endDate: end };
    }
    case "monthly": {
      const anchor = subMonths(referenceDate, offset);
      return {
        startDate: startOfMonth(anchor),
        endDate: endOfMonth(anchor),
      };
    }
    case "quarterly": {
      const anchor = subQuarters(referenceDate, offset);
      return {
        startDate: startOfQuarter(anchor),
        endDate: endOfQuarter(anchor),
      };
    }
    case "half_yearly": {
      const anchor = subMonths(referenceDate, offset * 6);
      const month = anchor.getMonth();
      const halfStartMonth = month < 6 ? 0 : 6;
      const year = anchor.getFullYear();
      const start = new Date(year, halfStartMonth, 1);
      const end = endOfMonth(new Date(year, halfStartMonth + 5, 1));
      return { startDate: start, endDate: end };
    }
    case "annually": {
      const anchor = subYears(referenceDate, offset);
      return {
        startDate: startOfYear(anchor),
        endDate: endOfYear(anchor),
      };
    }
    default:
      return { startDate: referenceDate, endDate: referenceDate };
  }
};

const getBucketDisplayLabel = (
  period: TimelyComparisonPeriod,
  startDate: Date,
  endDate: Date,
): string => {
  switch (period) {
    case "weekly": {
      if (startDate.getMonth() === endDate.getMonth()) {
        return `${format(startDate, "d")}-${format(endDate, "d MMM")}`;
      }
      return `${format(startDate, "d MMM")}-${format(endDate, "d MMM")}`;
    }
    case "monthly":
      return format(startDate, "MMM yyyy");
    case "quarterly":
      return format(startDate, "MMM yyyy");
    case "half_yearly":
      return format(startDate, "MMM yyyy");
    case "annually":
      return format(startDate, "yyyy");
    default:
      return format(startDate, "MMM yyyy");
  }
};

const seededValue = (seed: number, base: number, spread: number) => {
  const wave = Math.sin(seed * 1.7) * 0.5 + Math.cos(seed * 0.9) * 0.5;
  return Math.max(0, Math.round(base + wave * spread + (seed % 5)));
};

export interface BuildMockBucketsOptions {
  poolSize?: number;
  referenceDate?: Date;
  valueBase?: number;
  valueSpread?: number;
}

/**
 * Builds chronological historical buckets (oldest → newest).
 * Backend can return the same shape directly as pre-aggregated buckets.
 */
export const buildMockHistoricalBuckets = (
  period: TimelyComparisonPeriod,
  options: BuildMockBucketsOptions = {},
): TimelyComparisonBucket[] => {
  const referenceDate = options.referenceDate ?? new Date();
  const poolSize = options.poolSize ?? getDefaultHistoricalPoolSize(period);
  const valueBase = options.valueBase ?? 20;
  const valueSpread = options.valueSpread ?? 10;

  const buckets: TimelyComparisonBucket[] = [];

  for (let fromEnd = poolSize; fromEnd >= 1; fromEnd -= 1) {
    const labelIndex = poolSize - fromEnd + 1;
    const { startDate, endDate } = getBucketDateRange(
      period,
      fromEnd,
      referenceDate,
    );

    buckets.push({
      key: `${period}-${labelIndex}`,
      label: getBucketDisplayLabel(period, startDate, endDate),
      startDate: format(startDate, "dd MMM yyyy"),
      endDate: format(endDate, "dd MMM yyyy"),
      value: seededValue(labelIndex + valueBase, valueBase, valueSpread),
    });
  }

  return buckets;
};

/** Max buckets that fit clearly at the current chart width. */
export const getMaxVisibleBuckets = (
  chartWidth: number,
  period: TimelyComparisonPeriod,
): number => {
  const cap = MAX_VISIBLE_BUCKETS[period];

  if (chartWidth <= 0) {
    return cap;
  }

  const minPx = MIN_BUCKET_PX_BY_PERIOD[period];
  const calculated = Math.max(3, Math.floor(chartWidth / minPx));
  return Math.min(calculated, cap);
};

/**
 * Selects the most recent buckets that fit the chart width.
 * The newest bucket is always included.
 */
export const selectDisplayBuckets = (
  buckets: TimelyComparisonBucket[],
  chartWidth: number,
  period: TimelyComparisonPeriod,
): TimelyComparisonBucket[] => {
  if (!buckets.length) return [];

  const maxVisible = Math.min(
    buckets.length,
    getMaxVisibleBuckets(chartWidth, period),
  );
  if (buckets.length <= maxVisible) return buckets;

  return buckets.slice(buckets.length - maxVisible);
};

/**
 * Returns X-axis tick labels for visible buckets, thinned when still crowded.
 * The most recent label is always included.
 */
export const getVisibleXAxisTickLabels = (
  buckets: TimelyComparisonBucket[],
  chartWidth: number,
  period: TimelyComparisonPeriod,
): string[] => {
  if (!buckets.length) return [];

  const labels = buckets.map((bucket) => bucket.label);
  const minLabelPx = MIN_LABEL_PX_BY_PERIOD[period];
  const maxTicks = Math.max(
    2,
    Math.min(
      labels.length,
      Math.floor(chartWidth / minLabelPx) || labels.length,
    ),
  );

  if (labels.length <= maxTicks) return labels;

  const indexes = new Set<number>();
  indexes.add(0);
  indexes.add(labels.length - 1);

  for (let i = 1; i < maxTicks - 1; i += 1) {
    indexes.add(Math.round((i * (labels.length - 1)) / (maxTicks - 1)));
  }

  return Array.from(indexes)
    .sort((a, b) => a - b)
    .map((index) => labels[index]);
};

export interface TimelyChartLayoutConfig {
  useAngledTicks: boolean;
  margin: {
    top: number;
    right: number;
    left: number;
    bottom: number;
  };
  xAxis: {
    angle: number;
    textAnchor: "end" | "middle";
    height: number;
    dy: number;
  };
  cardPaddingBottom: number;
}

/** Shared chart layout — weekly/annually style applied to every period. */
export const getChartLayoutConfig = (
  bucketCount: number,
): TimelyChartLayoutConfig => {
  const useAngledTicks = bucketCount > 4;

  return {
    useAngledTicks,
    margin: {
      top: 14,
      right: 16,
      left: useAngledTicks ? 20 : 10,
      bottom: useAngledTicks ? 44 : 10,
    },
    xAxis: {
      angle: useAngledTicks ? -32 : 0,
      textAnchor: useAngledTicks ? "end" : "middle",
      height: useAngledTicks ? 56 : 32,
      dy: useAngledTicks ? 4 : 6,
    },
    cardPaddingBottom: useAngledTicks ? 2.25 : 1.5,
  };
};

/** Maps one metric's values onto the shared visible bucket timeline. */
export const projectSeriesOntoBuckets = (
  seriesBuckets: TimelyComparisonBucket[],
  templateBuckets: TimelyComparisonBucket[],
): TimelyComparisonBucket[] => {
  const valueByKey = new Map(
    seriesBuckets.map((bucket) => [bucket.key, bucket.value]),
  );

  return templateBuckets.map((template) => ({
    ...template,
    value: valueByKey.get(template.key) ?? 0,
  }));
};
